import { useEffect, useRef } from 'react'

import { shallowEqual, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { getUserType, getZendeskKey } from 'app/utils/widgets/zendeskConfig'
import { shouldShowZendeskOnRoute } from 'app/utils/widgets/zendeskVisibility'

// Redux state interface for the session slice
interface SessionState {
  currentAgency: { agencyID?: string } | null
  currentTeam: unknown | null
  currentUser: { email?: string; firstName?: string; lastName?: string } | null
}

interface RootState {
  session: SessionState
}

/**
 * Loads the Zendesk widget script with the specified key.
 * Identifies the user when the widget loads if user info is available.
 */
function loadZendeskScript(
  key: string,
  currentUser: SessionState['currentUser'],
): void {
  // Remove any existing script first
  const existingScript = document.getElementById('ze-snippet')
  if (existingScript) {
    existingScript.remove()
  }

  // Create and insert new script
  const script = document.createElement('script')
  script.id = 'ze-snippet'
  script.src = `https://static.zdassets.com/ekr/snippet.js?key=${key}`
  script.async = true

  // Identify user after widget loads (optional enhancement)
  script.onload = (): void => {
    // Give widget time to fully initialize
    setTimeout(() => {
      if (currentUser?.email && window.zE) {
        try {
          const name = [currentUser.firstName, currentUser.lastName]
            .filter(Boolean)
            .join(' ')
          window.zE?.('messenger:set', 'conversationFields', [
            { id: 'name', value: name },
            { id: 'email', value: currentUser.email || '' },
          ])
        } catch {
          // Zendesk may not support this method
        }
      }
    }, 1000)
  }

  // Handle script load failures gracefully
  script.onerror = (): void => {
    // Silent fail - widget just won't appear
    // Could add error reporting here if needed
  }

  document.head.appendChild(script)
}

/**
 * Unloads the Zendesk widget completely.
 * Removes the script, global variables, iframes, and launcher elements.
 * Critical: Must clean up ALL Zendesk internal state to allow re-initialization
 * with a different key (SPA user switching scenario).
 */
function unloadZendeskScript(): void {
  // Close widget if open
  if (window.zE) {
    try {
      window.zE('messenger', 'close')
    } catch {
      // Widget may not be fully initialized
    }
  }

  // Remove script
  const script = document.getElementById('ze-snippet')
  if (script) {
    script.remove()
  }

  // Remove ALL Zendesk-related globals
  // These internal variables prevent re-initialization in SPAs
  const zendeskGlobals = [
    'zE',
    'zESettings',
    'zEACLoaded',
    'zEmbed',
    'zEQueue',
    '__ZENDESK_CLIENT_I18N_GLOBAL',
    'Zendesk',
  ]
  zendeskGlobals.forEach((prop) => {
    try {
      delete (window as any)[prop]
    } catch {
      // Some properties may not be configurable
      ;(window as any)[prop] = undefined
    }
  })

  // Remove Zendesk iframes (including hidden ones)
  const zendeskIframes = document.querySelectorAll(
    'iframe[src*="zendesk"], iframe[title*="Zendesk"], iframe[name*="Zendesk"], iframe[id*="zendesk"], iframe[id*="webWidget"]',
  )
  zendeskIframes.forEach((iframe) => iframe.remove())

  // Remove Zendesk launcher and widget elements
  const zendeskElements = document.querySelectorAll(
    '[data-testid="launcher"], .zEWidget-launcher, #launcher, #webWidget, [id*="zendesk"], [class*="zEWidget"]',
  )
  zendeskElements.forEach((el) => el.remove())

  // Remove any Zendesk-injected style elements
  const zendeskStyles = document.querySelectorAll(
    'style[data-zendesk], link[href*="zendesk"]',
  )
  zendeskStyles.forEach((el) => el.remove())
}

/**
 * Manages Zendesk widget lifecycle based on user type and route.
 *
 * Script Loading Logic:
 * - Loads script for ANY authenticated user with valid Zendesk key
 * - PM users: Uses REACT_APP_ZENDESK_KEY_PM
 * - Renter users: Uses REACT_APP_ZENDESK_KEY_RENTER
 * - Unauthenticated users: Script NOT loaded
 *
 * Widget Visibility Logic (CSS):
 * - Shows widget when: hasTeamContext OR route matches allowlist
 * - Hides widget via CSS class (hide-zendesk) otherwise
 * - This allows Renter script to load while hiding widget until needed
 *
 * Lifecycle:
 * - Reloads script only when user type/key changes
 * - Cleans up on user logout or unmount
 * - Uses ref to prevent unnecessary script reloads
 */
export function ZendeskManager(): null {
  const location = useLocation()
  const { currentAgency, currentTeam, currentUser } = useSelector(
    (state: RootState) => ({
      currentAgency: state.session.currentAgency,
      currentTeam: state.session.currentTeam,
      currentUser: state.session.currentUser,
    }),
    shallowEqual,
  )

  const loadedKeyRef = useRef<string | null>(null)
  const userType = getUserType(currentAgency, currentUser)
  const targetKey = getZendeskKey(userType)

  useEffect(() => {
    const hasTeamContext = !!(currentAgency || currentTeam)
    const shouldShowOnRoute = shouldShowZendeskOnRoute(location.pathname)
    const isAuthenticated = !!currentUser

    // Script loading decision: authenticated user with valid key
    const shouldLoadScript = !!(currentUser && targetKey)

    // Widget visibility: show for ALL authenticated users (PM or Renter)
    // The appropriate widget key is already selected based on user type
    const shouldShowWidget =
      isAuthenticated || hasTeamContext || shouldShowOnRoute

    // Handle visibility via CSS class
    if (shouldShowWidget) {
      document.body.classList.remove('hide-zendesk')
    } else {
      document.body.classList.add('hide-zendesk')
    }

    // Handle script loading/unloading based on authentication + key
    if (shouldLoadScript) {
      // Only reload if the key has changed
      if (loadedKeyRef.current !== targetKey) {
        unloadZendeskScript()
        loadZendeskScript(targetKey, currentUser)
        loadedKeyRef.current = targetKey
      }
    } else if (!shouldLoadScript) {
      // Unload when user logs out or key becomes invalid
      if (loadedKeyRef.current) {
        unloadZendeskScript()
        loadedKeyRef.current = null
      }
    }
  }, [location.pathname, currentAgency, currentTeam, currentUser, targetKey])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unloadZendeskScript()
    }
  }, [])

  return null
}
